import { NAME_ID, ROLE_ID } from "shared/datamodel/schemas/org-chart";
import { measureText } from "./text-measurement";

import { TextConfig } from "konva/types/shapes/Text";
import { CSSProperties } from "react";
import { SizesConfig } from "frontend/data/shapes/sizes";
import { parseColor } from "utils/color-utils";

export type OrgChartNodeTextStyles = "h1" | "h2" | "sourceText" | "default";

export const DEFAULT_TEXT_COLOR = "#113255";

// Maximum number of lines before text gets ellipsed
export const MAX_TEXT_LINES = 3;

export const COMMON_NODE_TEXT_STYLES: TextConfig = {
  fontFamily: "Poppins",
  align: "center",
  verticalAlign: "top",
  wrap: "word",
  ellipsis: true,
  perfectDrawEnabled: false,
  lineHeight: 1.2, // Ensure consistent line height across all text styles
};

export const ORG_CHART_NODE_TEXT_STYLES: Record<OrgChartNodeTextStyles, TextConfig> = {
  h1: {
    ...COMMON_NODE_TEXT_STYLES,
    fontStyle: "700",
  },
  h2: {
    ...COMMON_NODE_TEXT_STYLES,
    fontStyle: "400",
  },
  sourceText: {
    ...COMMON_NODE_TEXT_STYLES,
    fontStyle: "700",
    lineHeight: 1.2,
    letterSpacing: 0.2,
    wrap: "word",
    ellipsis: true,
  },
  default: {
    ...COMMON_NODE_TEXT_STYLES,
    fontStyle: "400",
  },
};

const COMMON_TEXT_STYLE_FOR_HTML_INPUT: CSSProperties = {
  outline: "none",
  border: "none",
  margin: 0,
  padding: 0,
  width: "100%",
  height: "100%",
  verticalAlign: "middle",
  background: "unset",
  textAlign: "center",
  lineHeight: 1.2, // Match Konva Text lineHeight exactly
};

export const ORG_CHART_NODE_TEXT_STYLES_FOR_HTML: Record<OrgChartNodeTextStyles, CSSProperties> = {
  h1: {
    ...COMMON_TEXT_STYLE_FOR_HTML_INPUT,
    fontWeight: 700,
  },
  h2: {
    ...COMMON_TEXT_STYLE_FOR_HTML_INPUT,
    fontWeight: 400,
  },
  sourceText: {
    ...COMMON_TEXT_STYLE_FOR_HTML_INPUT,
    fontWeight: 700,
  },
  default: {
    ...COMMON_TEXT_STYLE_FOR_HTML_INPUT,
    textAlign: "center",
    fontWeight: 400,
    color: DEFAULT_TEXT_COLOR,
  },
};

export function getFillAndOpacity(initialValue: string, textStyleId: OrgChartNodeTextStyles, textColor: string) {
  if (textStyleId == "default") {
    return { fill: initialValue ? DEFAULT_TEXT_COLOR : "#848199", opacity: 1 };
  } else {
    return { fill: parseColor(textColor).color, opacity: initialValue ? 1 : 0.5 };
  }
}

/**
 * Calculate field heights and positions for all fields in a node
 */
export function calculateFieldLayout(
  data: Record<string, any>,
  fieldsToShow: string[],
  showImage: boolean,
  sizes: SizesConfig["orgChart"]
): {
  heights: Record<string, number>;
  positions: Record<string, number>;
  totalContentHeight: number;
  customFieldsStart: number;
  customFieldsEnd: number;
} {
  const heights: Record<string, number> = {};
  const positions: Record<string, number> = {};

  const topPadding = showImage ? sizes.thumbnailRadius : 0;
  let currentY = topPadding + 15;
  const textPadding = 4; // 4px padding on each side for text
  const textWidth = sizes.nodeWidth - textPadding * 2; // Available width for text after padding

  // Calculate heights and positions for fixed fields (NAME_ID, ROLE_ID)
  const nameHeight = measureText("h1", data?.[NAME_ID] || "Employee Name", textWidth).height;
  heights[NAME_ID] = nameHeight;
  positions[NAME_ID] = currentY;
  currentY += nameHeight + 2; // Small spacing between fields

  const roleHeight = measureText("h2", data?.[ROLE_ID] || "Role", textWidth).height;
  heights[ROLE_ID] = roleHeight;
  positions[ROLE_ID] = currentY;
  currentY += roleHeight + 8; // Larger spacing before custom fields

  // Track where custom fields section starts
  const customFieldsStart = currentY;

  // Calculate heights and positions for custom fields
  for (const fieldId of fieldsToShow) {
    const fieldHeight = measureText("default", data?.[fieldId] || " -- ", textWidth).height;
    heights[fieldId] = fieldHeight;
    positions[fieldId] = currentY + 6;
    currentY += fieldHeight + 2;
  }

  // If there are custom fields, adjust for the last field's spacing
  let customFieldsEnd = customFieldsStart;
  if (fieldsToShow.length > 0) {
    // Remove the last 2px spacing and add proper padding
    customFieldsEnd = currentY - 2;
  }

  const totalContentHeight = currentY + 10; // Add bottom padding

  return { heights, positions, totalContentHeight, customFieldsStart, customFieldsEnd };
}

/**
 * Get the stripe boundaries for the violet background section
 * Returns start position with 6px padding above custom fields and extends to bottom
 */
export function getStripeBoundaries(
  fieldLayout: ReturnType<typeof calculateFieldLayout>,
  fieldsToShow: string[]
): { stripeStart: number; stripeEnd: number } {
  // If no custom fields, no stripe
  if (fieldsToShow.length === 0) {
    return { stripeStart: 0, stripeEnd: 0 };
  }

  // Add 6px padding above the custom fields
  const STRIPE_PADDING = 6;
  const stripeStart = fieldLayout.customFieldsStart - STRIPE_PADDING;
  // Extend stripe to the total height (which already includes bottom padding)
  const stripeEnd = fieldLayout.totalContentHeight;

  return { stripeStart, stripeEnd };
}
